import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, ArrowLeft, User, Play, ChevronRight, X, RotateCcw, Dumbbell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AccountPage from "./AccountPage";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

// ── SPORTER TEGEL ─────────────────────────────────────────────────────────────
function SporterTegel({ sporter, onSelect }) {
  const initials = (sporter.naam || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const colors = ["#C9A84C", "#4C9A84", "#844CC9", "#C94C4C", "#4C84C9"];
  const color = colors[(sporter.naam || "").charCodeAt(0) % colors.length];

  return (
    <button
      data-testid={`sporter-tegel-${sporter.id}`}
      onClick={() => onSelect(sporter)}
      className="flex flex-col items-center gap-3 p-5 rounded-2xl border border-white/8 bg-white/3 hover:bg-white/6 hover:border-white/15 transition-all active:scale-95 group">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black text-black"
        style={{ background: color }}>
        {initials}
      </div>
      <div className="text-center">
        <p className="text-white font-bold text-sm leading-tight">{sporter.naam}</p>
        <p className="text-zinc-600 text-xs mt-1 truncate max-w-[120px]">{sporter.email}</p>
      </div>
      <div className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all group-hover:opacity-100 opacity-70"
        style={{ background: `${GOLD}15`, color: GOLD }}>
        <Play size={10} fill="currentColor" /> Selecteren
      </div>
    </button>
  );
}

// ── GROEP MODUS HOOFD PAGINA ────────────────────────────────────────────────
export default function GroepModus() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sporters, setSporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zoekterm, setZoekterm] = useState("");
  const [activeSporter, setActiveSporter] = useState(null);
  const [kioskToken, setKioskToken] = useState(null);
  const [starting, setStarting] = useState(false);

  // Check rechten: company_admin, trainer of super-admin
  const heeftToegang = ["company_admin", "trainer", "admin"].includes(user?.role);

  // Laad sporters van dit bedrijf
  useEffect(() => {
    if (!user?.token || !heeftToegang) return;
    fetch(`${API}/api/company/sporters`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then(r => r.json())
      .then(d => { setSporters(d.sporters || []); setLoading(false); })
      .catch(() => { setError("Sporters laden mislukt"); setLoading(false); });
  }, [user, heeftToegang]);

  const startSessie = useCallback(async (sporter) => {
    setStarting(true);
    try {
      const r = await fetch(`${API}/api/groep/start-session/${sporter.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${user.token}`, "Content-Type": "application/json" },
      });
      if (!r.ok) throw new Error("Sessie starten mislukt");
      const data = await r.json();
      setKioskToken(data.token);
      setActiveSporter(data.sporter);
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  }, [user]);

  const wisselSporter = useCallback(() => {
    setActiveSporter(null);
    setKioskToken(null);
  }, []);

  // Gefilterde sporters
  const gefilterд = sporters.filter(s =>
    !zoekterm || s.naam.toLowerCase().includes(zoekterm.toLowerCase()) || s.email.toLowerCase().includes(zoekterm.toLowerCase())
  );

  // Toegang geweigerd
  if (!heeftToegang) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <X size={28} className="text-red-400" />
          </div>
          <p className="text-white font-bold text-lg mb-2">Geen toegang</p>
          <p className="text-zinc-500 text-sm mb-6">Groep Modus is alleen voor trainers en bedrijfsbeheerders.</p>
          <button onClick={() => navigate("/")} className="px-6 py-3 rounded-xl bg-white/5 text-zinc-300 font-semibold text-sm hover:bg-white/10 transition-colors">
            Terug naar home
          </button>
        </div>
      </div>
    );
  }

  // Actieve sporter sessie — render volledige AccountPage met kiosk token
  if (activeSporter && kioskToken) {
    return (
      <div className="relative min-h-screen">
        {/* Floating Wissel Sporter knop */}
        <div className="fixed top-4 right-4 z-[100]">
          <button
            data-testid="wissel-sporter-btn"
            onClick={wisselSporter}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm shadow-xl transition-all active:scale-95 border border-white/15"
            style={{ background: "#0f0f0f" }}>
            <RotateCcw size={14} style={{ color: GOLD }} />
            <span className="text-white">Wissel sporter</span>
          </button>
        </div>
        {/* Sporter badge bovenaan */}
        <div className="fixed top-4 left-4 z-[100]">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 text-xs font-bold"
            style={{ background: "#0f0f0f" }}>
            <div className="w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black text-black"
              style={{ background: GOLD }}>
              {(activeSporter.naam || "?")[0].toUpperCase()}
            </div>
            <span className="text-white">{activeSporter.naam}</span>
            <span className="text-zinc-600 font-normal">Groep Modus</span>
          </div>
        </div>
        {/* Account pagina met kiosk token */}
        <KioskAccountView kioskToken={kioskToken} sporter={activeSporter} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] pb-10">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#080808]/95 backdrop-blur-md border-b border-white/5 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
            <ArrowLeft size={16} className="text-zinc-400" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}20` }}>
                <Users size={14} style={{ color: GOLD }} />
              </div>
              <h1 className="text-white font-black text-lg">Groep Modus</h1>
            </div>
            <p className="text-zinc-600 text-xs">Selecteer een sporter om te beginnen</p>
          </div>
          <div className="text-xs font-bold px-2.5 py-1 rounded-full"
            style={{ background: `${GOLD}15`, color: GOLD }}>
            {sporters.length} sporters
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6">
        {/* Zoekbalk */}
        <div className="relative mb-6">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            data-testid="sporter-zoekbalk"
            type="text"
            placeholder="Zoek op naam of e-mail..."
            value={zoekterm}
            onChange={e => setZoekterm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/8 bg-white/3 text-white text-sm placeholder:text-zinc-700 focus:outline-none focus:border-white/20 transition-colors"
          />
        </div>

        {/* Foutmelding */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Sporters grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 rounded-2xl bg-white/3 animate-pulse" />
            ))}
          </div>
        ) : gefilterд.length === 0 ? (
          <div className="text-center py-16">
            <Dumbbell size={40} className="text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 font-semibold">
              {zoekterm ? `Geen sporters gevonden voor "${zoekterm}"` : "Nog geen sporters aangemeld"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {gefilterд.map(s => (
              <SporterTegel
                key={s.id}
                sporter={s}
                onSelect={startSessie}
              />
            ))}
          </div>
        )}

        {starting && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-4"
                style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
              <p className="text-white font-bold">Sessie starten...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── KIOSK ACCOUNT VIEW ─────────────────────────────────────────────────────────
// Rendert de volledige AccountPage maar gebruikt een tijdelijk token
function KioskAccountView({ kioskToken, sporter }) {
  const [kioskUser, setKioskUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!kioskToken) return;
    fetch(`${API}/api/auth/me`, {
      headers: { Authorization: `Bearer ${kioskToken}` },
    })
      .then(r => r.json())
      .then(d => {
        setKioskUser({ ...d, token: kioskToken });
        setLoading(false);
      })
      .catch(() => {
        setError("Sporter dashboard laden mislukt");
        setLoading(false);
      });
  }, [kioskToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  return <AccountPage kioskUser={kioskUser} />;
}
